import React, { useEffect, useState, useCallback } from "react";
import { api, fmtINR, fmtDate } from "@/lib/api";
import { Receipt, Buildings, Storefront, BellRinging } from "@phosphor-icons/react";
import PaymentTasksPanel from "@/components/PaymentTasksPanel";

const CAT_META = {
  statutory: {
    label: "Statutory",
    icon: <Buildings size={14} weight="duotone" />,
    grad: "from-violet-500 to-purple-700",
    iconBg: "bg-violet-100 text-violet-700",
  },
  vendor: {
    label: "Vendor",
    icon: <Storefront size={14} weight="duotone" />,
    grad: "from-amber-500 to-orange-600",
    iconBg: "bg-amber-100 text-amber-800",
  },
  rent: {
    label: "Rent",
    icon: <Receipt size={14} weight="duotone" />,
    grad: "from-sky-500 to-cyan-600",
    iconBg: "bg-sky-100 text-sky-700",
  },
};

export default function AlertsTab({ refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get("/alerts"); setItems(res.data || []); }
    catch { setItems([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const grouped = items.reduce((acc, it) => { (acc[it.category] = acc[it.category] || []).push(it); return acc; }, {});
  const critical = items.filter((i) => i.severity === "critical").length;
  const totalDue = items.reduce((s, i) => s + i.amount, 0);

  return (
    <div data-testid="alerts-tab" className="space-y-5">
      <PaymentTasksPanel refreshKey={refreshKey} />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-sm grid place-items-center bg-amber-100 text-amber-700"><BellRinging size={15} weight="duotone" /></div>
            <h2 className="font-heading font-bold text-2xl tracking-tight">Outstanding &amp; alerts</h2>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <div className="px-2.5 py-1 rounded-sm border bg-rose-50 text-rose-700 border-rose-200 text-xs flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider font-bold opacity-70">Critical</span>
              <span className="font-mono tabular-nums font-bold">{critical}</span>
            </div>
            <div className="px-2.5 py-1 rounded-sm border bg-slate-100 text-slate-700 border-slate-200 text-xs flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider font-bold opacity-70">Total due</span>
              <span className="font-mono tabular-nums font-bold">{fmtINR(totalDue)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {["statutory", "vendor", "rent"].map((cat) => {
          const meta = CAT_META[cat];
          const list = grouped[cat] || [];
          const colTotal = list.reduce((s, i) => s + i.amount, 0);
          return (
            <div key={cat} className="bg-white rounded-md border border-slate-200 overflow-hidden shadow-sm flex flex-col" data-testid={`alerts-col-${cat}`}>
              <div className={`bg-gradient-to-r ${meta.grad} text-white px-4 py-3`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-7 w-7 grid place-items-center rounded-sm bg-white/15 backdrop-blur-sm">{meta.icon}</div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/90">{meta.label}</div>
                      <div className="font-mono tabular-nums text-sm font-bold mt-0.5">{fmtINR(colTotal)}</div>
                    </div>
                  </div>
                  <div className="text-xs font-mono tabular-nums text-white/80">{list.length}</div>
                </div>
              </div>
              <div className="p-1 divide-y divide-slate-100 flex-1">
                {loading && <div className="text-xs text-slate-500 p-4">Loading…</div>}
                {!loading && list.length === 0 && <div className="text-xs text-slate-500 p-4">Nothing pending.</div>}
                {list.map((it) => <AlertRow key={it.id} it={it} />)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AlertRow({ it }) {
  const stripe = {
    critical: "border-l-rose-500 bg-rose-50/40",
    warning: "border-l-amber-500 bg-amber-50/40",
    info: "border-l-sky-400 bg-white",
  }[it.severity];
  const pill = {
    critical: "bg-rose-100 text-rose-700",
    warning: "bg-amber-100 text-amber-800",
    info: "bg-slate-100 text-slate-600",
  }[it.severity];

  return (
    <div className={`border-l-4 ${stripe} px-3 py-2.5`} data-testid={`alert-row-${it.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium leading-tight text-slate-900">{it.label}</div>
          <div className="text-[11px] mt-1 flex items-center gap-2">
            <span className={`${pill} px-1.5 py-0.5 rounded-sm font-bold uppercase tracking-wider text-[10px]`}>
              {it.days_left < 0 ? `Overdue ${Math.abs(it.days_left)}d` : `Due in ${it.days_left}d`}
            </span>
            <span className="text-slate-400">{fmtDate(it.due_date)}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono tabular-nums text-sm font-semibold text-slate-900">{fmtINR(it.amount)}</div>
        </div>
      </div>
    </div>
  );
}
